import api from './api';
import { getApiUrl } from './config';
import { endpoint } from './endpoints';
import { subscriptionService } from './subscriptionService';
import { handleApiCall } from '../utils/apiResponseHandler';

export interface PhoneNumberPricingInfo {
  freePhoneNumbers: number;
  additionalPhoneNumberPrice: number;
  currency: string;
  userHasFreePhone: boolean;
  totalPhoneNumbers: number;
  canAddMore: boolean;
}

export interface AssignPhoneNumberRequest {
  areaCode: number;
  state: string;
  phoneNumber?: string;
}

export interface AssignPhoneNumberResponse {
  success: boolean;
  message: string;
  data: {
    phoneNumber: {
      id: string;
      number: string;
      areaCode: number;
      state: string;
      isActive: boolean;
      phoneSid: string;
    };
  };
}

class PhoneNumberPricingService {
  /**
   * Get current user's phone number pricing information
   */
  async getUserPricingInfo(): Promise<PhoneNumberPricingInfo> {
    try {
      // First, get the user's current subscription to determine pricing
      const subscription = await subscriptionService.getMyActiveSubscription();
      
      // Determine phone number pricing based on subscription plan
      let additionalPhoneNumberPrice = 4.00; // Default fallback

      // Helper: infer price from user context (accountType) if available
      const inferPriceFromUserContext = (): number | null => {
        if (typeof window === 'undefined') return null;
        try {
          const ctxRaw = sessionStorage.getItem('insuracentral_user_context');
          if (!ctxRaw) return null;
          const ctx = JSON.parse(ctxRaw) as { accountType?: string };
          const accountType = (ctx?.accountType || '').toLowerCase();
          if (accountType.includes('full')) return 2.00;
          if (accountType.includes('basic')) return 4.00;
          return null;
        } catch {
          return null;
        }
      };
      
      if (subscription?.plan) {
        // Check if the plan has area_code_pricing in metadata
        const areaCodePricing = (subscription.plan as any).metadata?.area_code_pricing as number;
        if (areaCodePricing && areaCodePricing > 0) {
          additionalPhoneNumberPrice = areaCodePricing;
        } else {
          // Fallback to plan name-based pricing
          const planName = (subscription.plan.name || '').toLowerCase();
          if (planName.includes('basic')) {
            additionalPhoneNumberPrice = 4.00;
          } else if (planName.includes('full') || planName.includes('fledged')) {
            additionalPhoneNumberPrice = 2.00;
          } else {
            // Secondary fallback: infer from user context accountType
            const inferred = inferPriceFromUserContext();
            if (inferred !== null) additionalPhoneNumberPrice = inferred;
          }
        }
      } else {
        // No subscription: infer from user context accountType if possible
        const inferred = inferPriceFromUserContext();
        if (inferred !== null) additionalPhoneNumberPrice = inferred;
      }

      const result = await handleApiCall<{
        statusCode: number;
        message: string;
        data: PhoneNumberPricingInfo;
        timestamp: string;
        timeTakenMs: number;
      }>(async () => {
        const response = await api.get<{
          statusCode: number;
          message: string;
          data: PhoneNumberPricingInfo;
          timestamp: string;
          timeTakenMs: number;
        }>({
          url: getApiUrl(endpoint.phoneNumberPricing.getPricing),
          auth: true,
        });
        return response;
      });

      // Standardized response handling - expect nested data structure
      // Triple-nested: { data: { data: { data: {...} } } }
      if ((result as any).data?.data?.data && typeof (result as any).data.data.data === 'object') {
        const pricingInfo = (result as any).data.data.data;
        if ('freePhoneNumbers' in pricingInfo || 'totalPhoneNumbers' in pricingInfo) {
          return {
            ...pricingInfo,
            additionalPhoneNumberPrice,
          };
        }
      }
      // Double-nested: { data: { data: {...} } }
      if ((result as any).data?.data && typeof (result as any).data.data === 'object') {
        const pricingInfo = (result as any).data.data;
        if ('freePhoneNumbers' in pricingInfo || 'totalPhoneNumbers' in pricingInfo) {
          return {
            ...pricingInfo,
            additionalPhoneNumberPrice,
          };
        }
      }
      // Handle direct data response (no wrapper)
      if ((result as any).data && typeof (result as any).data === 'object' && 'freePhoneNumbers' in (result as any).data) {
        const pricingInfo = (result as any).data as unknown as PhoneNumberPricingInfo;
        return {
          ...pricingInfo,
          additionalPhoneNumberPrice,
        };
      }

      // Return default values if endpoint not implemented
      
      return {
        freePhoneNumbers: 0,
        additionalPhoneNumberPrice,
        currency: 'USD',
        userHasFreePhone: false,
        totalPhoneNumbers: 0,
        canAddMore: true,
      };
    } catch (error) {

      // Try to get subscription-based pricing even if API fails
      try {
        const subscription = await subscriptionService.getMyActiveSubscription();
        let additionalPhoneNumberPrice = 4.00; // Default fallback
        
        // Helper: infer price from user context when no clear plan info
        const inferPriceFromUserContext = (): number | null => {
          if (typeof window === 'undefined') return null;
          try {
            const ctxRaw = sessionStorage.getItem('insuracentral_user_context');
            if (!ctxRaw) return null;
            const ctx = JSON.parse(ctxRaw) as { accountType?: string };
            const accountType = (ctx?.accountType || '').toLowerCase();
            if (accountType.includes('full')) return 2.00;
            if (accountType.includes('basic')) return 4.00;
            return null;
          } catch {
            return null;
          }
        };
        
        if (subscription?.plan) {
          const areaCodePricing = (subscription.plan as any).metadata?.area_code_pricing as number;
          if (areaCodePricing && areaCodePricing > 0) {
            additionalPhoneNumberPrice = areaCodePricing;
          } else {
            const planName = (subscription.plan.name || '').toLowerCase();
            if (planName.includes('basic')) {
              additionalPhoneNumberPrice = 4.00;
            } else if (planName.includes('full') || planName.includes('fledged')) {
              additionalPhoneNumberPrice = 2.00;
            } else {
              const inferred = inferPriceFromUserContext();
              if (inferred !== null) additionalPhoneNumberPrice = inferred;
            }
          }
        } else {
          const inferred = inferPriceFromUserContext();
          if (inferred !== null) additionalPhoneNumberPrice = inferred;
        }
        
        return {
          freePhoneNumbers: 0,
          additionalPhoneNumberPrice,
          currency: 'USD',
          userHasFreePhone: false,
          totalPhoneNumbers: 0,
          canAddMore: true,
        };
      } catch (subscriptionError) {

        // Final fallback defaults
        return {
          freePhoneNumbers: 0,
          additionalPhoneNumberPrice: 4.00,
          currency: 'USD',
          userHasFreePhone: false,
          totalPhoneNumbers: 0,
          canAddMore: true,
        };
      }
    }
  }

  /**
   * Assign a new phone number to the current user
   */
  async assignPhoneNumber(request: AssignPhoneNumberRequest): Promise<AssignPhoneNumberResponse> {
    try {
      const result = await handleApiCall<{
        statusCode: number;
        message: string;
        data: {
          phoneNumber: {
            id: string;
            number: string;
            areaCode: number;
            state: string;
            isActive: boolean;
            phoneSid: string;
          };
        };
        timestamp: string;
        timeTakenMs: number;
      }>(async () => {
        const response = await api.post<{
          statusCode: number;
          message: string;
          data: {
            phoneNumber: {
              id: string;
              number: string;
              areaCode: number;
              state: string;
              isActive: boolean;
              phoneSid: string;
            };
          };
          timestamp: string;
          timeTakenMs: number;
        }>({
          url: getApiUrl(endpoint.phoneNumberPricing.assignPhoneNumber),
          body: {
            phoneNumber: request.phoneNumber,
            areaCode: request.areaCode,
            state: request.state,
          },
          auth: true,
        });
        return response;
      });

      if (!(result as any).data?.data) {

        throw new Error('Invalid response format from phone number assignment API');
      }

      const response = {
        success: true,
        message: (result as any).data.message,
        data: (result as any).data.data,
      };

      return response;
    } catch (error) {

      throw new Error('Failed to assign phone number');
    }
  }

  /**
   * Get user's current phone numbers
   */
  async getUserPhoneNumbers(): Promise<any[]> {
    try {
      const result = await handleApiCall<{
        statusCode: number;
        message: string;
        data: any[];
        timestamp: string;
        timeTakenMs: number;
      }>(async () => {
        const response = await api.get<{
          statusCode: number;
          message: string;
          data: any[];
          timestamp: string;
          timeTakenMs: number;
        }>({
          // limit=1000: the BE endpoint defaults to page=1&limit=10 — users with
          // big number banks (Anersi: 110 active) only ever saw 10 in Manage
          // Numbers, with no pagination UI (2026-07-24). 1000 gives headroom for
          // the biggest realistic banks; real pagination if anyone outgrows it.
          url: getApiUrl(endpoint.phoneNumberPricing.getUserPhoneNumbers) + '?page=1&limit=1000',
          auth: true,
        });
        return response;
      });

      // Handle different response formats with comprehensive checks
      // Check if result.items exists and has an items array inside it (nested structure)
      if ((result as any).items?.items && Array.isArray((result as any).items.items)) {
        return (result as any).items.items;
      }
      // MOST COMMON: After handleApiCall unwrapping: { items: [...], meta: {...} }
      if (Array.isArray((result as any).items)) {
        return (result as any).items;
      }
      // Double-nested wrapper: { data: { data: { items: [...] } } }
      if ((result as any).data?.data?.items && Array.isArray((result as any).data.data.items)) {
        return (result as any).data.data.items;
      }
      // Standard format: { data: { items: [...] } }
      if ((result as any).data?.items && Array.isArray((result as any).data.items)) {
        return (result as any).data.items;
      }
      // Direct array: { data: [...] }
      if (Array.isArray(result.data)) {
        return result.data;
      }
      // Nested wrapper: { data: { data: [...] } }
      if ((result as any).data?.data && Array.isArray((result as any).data.data)) {
        return (result as any).data.data;
      }
      // Legacy phoneNumbers: { data: { phoneNumbers: [...] } }
      if ((result as any).data?.phoneNumbers && Array.isArray((result as any).data.phoneNumbers)) {
        return (result as any).data.phoneNumbers;
      }
      if (Array.isArray((result as any).phoneNumbers)) {
        return (result as any).phoneNumbers;
      }

      // Handle empty or null data - this is valid, user may not have any phone numbers
      if (!result.data && !(result as any).items) {
        return [];
      }

      // Use type assertion to check if it's an empty array
      const data = result.data as any;
      if (Array.isArray(data) && data.length === 0) {
        return [];
      }

      return [];
    } catch (error) {

      throw new Error('Failed to fetch phone numbers');
    }
  }

  /**
   * Get agency phone numbers (owner/admin scope). If agencyId omitted, uses current agency via header.
   */
  async getAgencyPhoneNumbers(params?: { agencyId?: string; page?: number; limit?: number; includeDeleted?: boolean; deletedOnly?: boolean }): Promise<any[]> {
    try {
      const query = new URLSearchParams();
      if (params?.agencyId) query.append('agencyId', params.agencyId);
      if (params?.page) query.append('page', String(params.page));
      if (params?.limit) query.append('limit', String(params.limit));
      if (params?.includeDeleted) query.append('includeDeleted', 'true');
      if (params?.deletedOnly) query.append('deletedOnly', 'true');
      const url = `${getApiUrl(endpoint.phoneNumberPricing.getUserPhoneNumbers)}${query.toString() ? `?${query.toString()}` : ''}`;
      const res = await api.get<{ statusCode: number; data: { items: any[]; meta: any } }>({ url, auth: true });
      // API returns { data: { items: [...], meta: {...} } }
      return (res as any).data?.data?.items || (res as any).data?.items || [];
    } catch (error) {

      throw new Error('Failed to fetch agency phone numbers');
    }
  }

  /** Soft delete (schedule 90-day retrieval) */
  async softDeletePhoneNumber(phoneId: string): Promise<boolean> {
    try {
      const res = await api.delete({
        url: getApiUrl(endpoint.communication.getPhoneNumbers + `/${phoneId}`),
        auth: true,
      });
      return !!(res as any)?.data && ((res as any).data.statusCode === 200 || (res as any).status === 200);
    } catch (error) {

      return false;
    }
  }

  /** Restore within retention window */
  async restorePhoneNumber(phoneId: string): Promise<boolean> {
    try {
      const res = await api.post({
        url: getApiUrl(endpoint.phoneNumberPricing.restorePhoneNumber(phoneId)),
        body: {},
        auth: true,
      });
      return !!(res as any)?.data && ((res as any).data.statusCode === 200 || (res as any).status === 200);
    } catch (error) {

      return false;
    }
  }

  /** Reassign phone number to another user */
  async reassignPhoneNumber(phoneId: string, targetUserId: string): Promise<boolean> {
    try {
      const res = await api.post({
        url: getApiUrl(endpoint.phoneNumberPricing.reassignPhoneNumber(phoneId)),
        body: { userId: targetUserId },
        auth: true,
      });
      return !!(res as any)?.data && ((res as any).data.statusCode === 200 || (res as any).status === 200);
    } catch (error) {

      return false;
    }
  }

  /**
   * Get available phone numbers from backend (with area codes)
   */
  async getAvailablePhoneNumbers(params?: {
    areaCode?: string;
    state?: string;
    limit?: number;
  }): Promise<any[]> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.areaCode) queryParams.append('areaCode', params.areaCode);
      if (params?.state) queryParams.append('state', params.state);
      if (params?.limit) queryParams.append('limit', params.limit.toString());

      const url = getApiUrl(endpoint.phoneNumberPricing.getAvailablePhoneNumbers(queryParams.toString()));

      const result = await handleApiCall<{
        statusCode: number;
        message: string;
        data: any;
        timestamp: string;
        timeTakenMs: number;
      }>(async () => {
        const response = await api.get<{
          statusCode: number;
          message: string;
          data: any;
          timestamp: string;
          timeTakenMs: number;
        }>({
          url,
          auth: true,
        });
        return response;
      });

      // Resilience guard for multiple response shapes
      const payload = (result as any)?.data;
      // Shape A: { data: { availableNumbers: [...] } }
      if (payload?.data && Array.isArray(payload.data.availableNumbers)) {
        return payload.data.availableNumbers;
      }
      // Shape B: { data: [...] }
      if (payload?.data && Array.isArray(payload.data)) {
        return payload.data;
      }
      // Shape C: direct array on top-level payload
      if (Array.isArray(payload)) {
        return payload;
      }
      // Shape D: direct availableNumbers at top-level
      if (Array.isArray(payload?.availableNumbers)) {
        return payload.availableNumbers;
      }
      return [];
    } catch (error) {

      throw new Error('Failed to fetch available phone numbers');
    }
  }

  /**
   * Format pricing display text based on user's plan
   */
  formatPricingDisplay(pricingInfo: PhoneNumberPricingInfo): string {
    if (pricingInfo.freePhoneNumbers === 999999) {
      // Unlimited plan (TRIAL or ENTERPRISE)
      return 'Unlimited phone numbers included';
    }
    
    if (pricingInfo.additionalPhoneNumberPrice === 0) {
      // Free phone numbers
      return 'Phone numbers are free';
    }
    
    // User needs to pay for phone numbers
    return `Phone numbers: $${pricingInfo.additionalPhoneNumberPrice} per month`;
  }

  /**
   * Get confirmation message for adding a phone number
   */
  getAddPhoneConfirmationMessage(pricingInfo: PhoneNumberPricingInfo): string | null {
    // No confirmation needed for free numbers
    if (pricingInfo.additionalPhoneNumberPrice === 0) {
      return null;
    }

    return `This will cost $${pricingInfo.additionalPhoneNumberPrice} per month. Continue?`;
  }
}

export const phoneNumberPricingService = new PhoneNumberPricingService();
export default phoneNumberPricingService;